import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'

/**
 * ArtemisChaseCameraView - Copies WebGPU render target to HTML canvas
 * Uses GPUCommandEncoder.copyTextureToBuffer to extract pixels without post-processing
 */
export function ArtemisChaseCameraView({ renderTarget, isActive, artemisProgress = 0 }) {
  const canvasRef = useRef(null)
  const staticCanvasRef = useRef(null)
  const gpuBufferRef = useRef(null)
  const isMappingRef = useRef(false)
  const isMountedRef = useRef(true)
  const animationFrameRef = useRef(null)
  
  // Check if we're in signal loss range
  const isSignalLost = (artemisProgress >= 74 && artemisProgress <= 76) || 
                       (artemisProgress >= 98 && artemisProgress <= 100)
  
  useEffect(() => {
    isMountedRef.current = true
    
    if (!isActive) return
    
    // Create main canvas element
    const canvas = document.createElement('canvas')
    canvas.width = 320
    canvas.height = 240
    canvas.className = 'chase-camera-view'
    
    document.body.appendChild(canvas)
    canvasRef.current = canvas
    
    // Create static overlay canvas - positioned after main canvas exists
    const staticCanvas = document.createElement('canvas')
    staticCanvas.width = 320
    staticCanvas.height = 240
    staticCanvas.className = 'chase-camera-static'
    staticCanvas.style.position = 'fixed'
    staticCanvas.style.pointerEvents = 'none'
    staticCanvas.style.zIndex = '1501'
    staticCanvas.style.opacity = '0'
    staticCanvas.style.transition = 'opacity 0.3s ease'
    
    document.body.appendChild(staticCanvas)
    staticCanvasRef.current = staticCanvas
    
    // Copy position from main canvas after it's been positioned
    const updateStaticPosition = () => {
      if (canvas && staticCanvas) {
        const rect = canvas.getBoundingClientRect()
        staticCanvas.style.left = `${rect.left}px`
        staticCanvas.style.top = `${rect.top}px`
        staticCanvas.style.width = `${rect.width}px`
        staticCanvas.style.height = `${rect.height}px`
      }
    }
    
    // Initial position update
    setTimeout(updateStaticPosition, 100)
    
    // Update position on window resize
    window.addEventListener('resize', updateStaticPosition)
    
    return () => {
      isMountedRef.current = false
      
      // Remove resize listener
      window.removeEventListener('resize', updateStaticPosition)
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (canvasRef.current) {
        document.body.removeChild(canvasRef.current)
        canvasRef.current = null
      }
      if (staticCanvasRef.current) {
        document.body.removeChild(staticCanvasRef.current)
        staticCanvasRef.current = null
      }
      if (gpuBufferRef.current) {
        gpuBufferRef.current.destroy()
        gpuBufferRef.current = null
      }
      isMappingRef.current = false
    }
  }, [isActive])
  
  // Animate static effect when signal is lost
  useEffect(() => {
    if (!staticCanvasRef.current || !isActive) return
    
    const staticCanvas = staticCanvasRef.current
    const staticCtx = staticCanvas.getContext('2d')
    
    if (isSignalLost) {
      staticCanvas.style.opacity = '1'
      
      const drawStatic = () => {
        if (!isMountedRef.current || !isSignalLost) return
        
        const width = staticCanvas.width
        const height = staticCanvas.height
        const imageData = staticCtx.createImageData(width, height)
        const data = imageData.data
        const dataLength = data.length
        
        // Pre-calculate values outside loops
        const time = Date.now() * 0.001
        const glitchIntensity = Math.sin(time * 3) * 0.5 + 0.5
        const glitchThreshold = 0.15 * glitchIntensity
        
        // Optimized: Single pass through pixels with reduced calculations
        let i = 0
        for (let y = 0; y < height; y++) {
          // Pre-calculate per-row values
          const isEvenRow = (y & 1) === 0 // Bitwise AND faster than modulo
          const scanLineMod = isEvenRow ? 0.85 : 1.0
          const isGlitchLine = Math.random() < glitchThreshold
          const lineShift = isGlitchLine ? ((Math.random() * 30 - 15) | 0) : 0 // | 0 faster than Math.floor
          
          for (let x = 0; x < width; x++, i += 4) {
            // Base noise value
            const baseValue = (30 + Math.random() * 60) * scanLineMod
            
            if (isGlitchLine && x + lineShift >= 0 && x + lineShift < width) {
              // Glitch line with RGB separation (simplified)
              const rand = Math.random()
              data[i] = rand < 0.3 ? 255 : baseValue
              data[i + 1] = baseValue * 0.9
              data[i + 2] = rand > 0.7 ? 255 : baseValue * 1.1
            } else {
              // Normal grayscale
              data[i] = data[i + 1] = data[i + 2] = baseValue
            }
            data[i + 3] = 255
          }
        }
        
        // Reduced frequency block glitches (10% instead of 20%)
        if (Math.random() < 0.1) {
          const blockX = (Math.random() * width) | 0
          const blockY = (Math.random() * height) | 0
          const blockW = (Math.random() * 30 + 15) | 0
          const blockH = (Math.random() * 15 + 5) | 0
          const maxY = Math.min(blockY + blockH, height)
          const maxX = Math.min(blockX + blockW, width)
          
          for (let y = blockY; y < maxY; y++) {
            const rowOffset = y * width * 4
            for (let x = blockX; x < maxX; x++) {
              const i = rowOffset + x * 4
              const rand = Math.random()
              data[i] = rand < 0.5 ? 255 : 0
              data[i + 1] = rand < 0.5 ? 255 : 0
              data[i + 2] = rand > 0.5 ? 255 : 0
            }
          }
        }
        
        staticCtx.putImageData(imageData, 0, 0)
        
        // Add "SIGNAL LOST" text with digital glitch
        staticCtx.fillStyle = '#EE3A17'
        staticCtx.font = 'bold 20px monospace'
        staticCtx.textAlign = 'center'
        staticCtx.textBaseline = 'middle'
        
        // Digital glitch - random position offset with occasional large jumps
        const glitchX = Math.random() < 0.1 ? (Math.random() - 0.5) * 20 : (Math.random() - 0.5) * 4
        const glitchY = Math.random() < 0.1 ? (Math.random() - 0.5) * 20 : (Math.random() - 0.5) * 4
        
        // Draw with RGB separation occasionally
        if (Math.random() < 0.3) {
          staticCtx.fillStyle = 'rgba(255, 0, 0, 0.7)'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX - 2, staticCanvas.height / 2 + glitchY)
          staticCtx.fillStyle = 'rgba(0, 255, 255, 0.7)'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX + 2, staticCanvas.height / 2 + glitchY)
        } else {
          staticCtx.fillStyle = '#EE3A17'
          staticCtx.fillText('SIGNAL LOST', staticCanvas.width / 2 + glitchX, staticCanvas.height / 2 + glitchY)
        }
        
        // Continue animation
        animationFrameRef.current = requestAnimationFrame(drawStatic)
      }
      
      drawStatic()
    } else {
      staticCanvas.style.opacity = '0'
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isSignalLost, isActive])
  
  // Copy GPU texture to canvas - no throttling, sRGB handled at render target
  useFrame(({ gl }) => {
    if (!canvasRef.current || !isActive) return
    if (!renderTarget) return
    
    // Skip if already processing - don't queue up operations
    if (isMappingRef.current) return
    
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    try {
      // Get the WebGPU backend and device
      const backend = gl.backend
      if (!backend || !backend.device) return
      
      const device = backend.device
      
      // Check if renderTarget has texture property
      if (!renderTarget.texture) return
      
      const gpuTexture = backend.get(renderTarget.texture).texture
      
      if (!gpuTexture) return
      
      // WebGLRenderTarget stores dimensions in .width and .height properties
      const width = renderTarget.width || renderTarget.texture.image?.width || 320
      const height = renderTarget.height || renderTarget.texture.image?.height || 240
      
      // Calculate buffer size with proper alignment (256 bytes for WebGPU)
      const bytesPerPixel = 4 // RGBA
      const unalignedBytesPerRow = width * bytesPerPixel
      const bytesPerRow = Math.ceil(unalignedBytesPerRow / 256) * 256
      const bufferSize = bytesPerRow * height
      
      // Validate values
      if (!Number.isFinite(bufferSize) || bufferSize <= 0) return
      
      // Create or reuse GPU buffer for reading pixels
      if (!gpuBufferRef.current || gpuBufferRef.current.size !== bufferSize) {
        // If buffer exists, wait to destroy it until it's safe
        const oldBuffer = gpuBufferRef.current
        
        // Create new buffer first
        gpuBufferRef.current = device.createBuffer({
          size: bufferSize,
          usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
        })
        
        // Schedule old buffer destruction after any pending operations
        if (oldBuffer) {
          // Destroy asynchronously to avoid conflicts
          setTimeout(() => {
            try {
              if (oldBuffer.mapState === 'mapped') {
                oldBuffer.unmap()
              }
              oldBuffer.destroy()
            } catch (e) {
              // Buffer already destroyed, ignore
            }
          }, 100)
        }
      }
      
      const buffer = gpuBufferRef.current
      
      // Double-check buffer state - skip if not ready
      if (buffer.mapState !== 'unmapped') return
      
      // Mark as busy
      isMappingRef.current = true
      
      // Create command encoder
      const commandEncoder = device.createCommandEncoder()
      
      // Copy texture to buffer
      commandEncoder.copyTextureToBuffer(
        { texture: gpuTexture },
        { buffer, bytesPerRow },
        { width, height }
      )
      
      // Submit commands
      device.queue.submit([commandEncoder.finish()])
      
      // Read buffer asynchronously - don't wait for GPU
      buffer.mapAsync(GPUMapMode.READ).then(() => {
        if (!isMountedRef.current) {
          buffer.unmap()
          isMappingRef.current = false
          return
        }
        
        const arrayBuffer = buffer.getMappedRange()
        const pixels = new Uint8Array(arrayBuffer)
        
        // Create ImageData
        const imageData = ctx.createImageData(width, height)
        const data = imageData.data
        
        // Direct pixel copy - flip vertically, no color correction (sRGB handled at render target)
        let srcY = 0
        let dstY = height - 1
        
        for (let y = 0; y < height; y++, srcY++, dstY--) {
          const srcRowStart = srcY * bytesPerRow
          const dstRowStart = dstY * width * 4
          
          // Direct copy - render target is already in sRGB space
          for (let x = 0; x < width; x++) {
            const srcIdx = srcRowStart + (x * 4)
            const dstIdx = dstRowStart + (x * 4)
            
            // Direct copy - no color correction needed
            data[dstIdx] = pixels[srcIdx]
            data[dstIdx + 1] = pixels[srcIdx + 1]
            data[dstIdx + 2] = pixels[srcIdx + 2]
            data[dstIdx + 3] = 255
          }
        }
        
        // Fill canvas with black background first
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, width, height)
        
        // Draw to canvas
        ctx.putImageData(imageData, 0, 0)
        
        // Unmap buffer and mark as ready
        buffer.unmap()
        isMappingRef.current = false
      }).catch(() => {
        isMappingRef.current = false
      })
      
    } catch (error) {
      isMappingRef.current = false
    }
  })
  
  return null
}